import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Dimmer, Icon, Loader, Message } from 'semantic-ui-react';
import { Query } from 'stores/dataStore';
import { ChecklistReportStore } from 'stores/checklist-reports/checklist-report-store';
import { buildChecklistReportFilter } from './checklist-report-query';

interface ChecklistReportListMobileProps extends RouteComponentProps {
  checklistReportStore?: ChecklistReportStore;
  filters?: Record<string, any>;
  onReset?: () => void;
}

const DEFAULT_QUERY_PARAMETERS = {
  ShowDeleted: 'false'
};

const ChecklistReportListMobile: FC<ChecklistReportListMobileProps> = ({ checklistReportStore, filters, history, onReset }) => {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(0);
  const [grouped, setGrouped] = useState(false);
  const prevFilters = useRef(filters);
  const take = 10;

  const buildQuery = (): Query => ({
    searchQuery: '',
    orderBy: [{ direction: 'Descending', field: 'eventId', useProfile: false }],
    skip,
    take,
    filter: buildChecklistReportFilter(filters || {}),
    parameters: DEFAULT_QUERY_PARAMETERS
  });

  useEffect(() => {
    if (prevFilters.current !== filters) {
      prevFilters.current = filters;
      // Changing skip re-runs this effect, which then loads page 1.
      if (skip !== 0) {
        setSkip(0);
        return;
      }
    }
    checklistReportStore.getFromEndpointAsync(buildQuery(), grouped ? 'get-checklist-reports-grouped' : 'get-checklist-reports');
  }, [filters, skip, grouped]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleViewMode = () => {
    setSkip(0);
    setGrouped(value => !value);
  };

  const handleExport = () => {
    checklistReportStore.exportToExcel(buildQuery()).catch(() => undefined);
  };

  const openEvent = (eventGuid: string) =>
    history.push(`/events/${eventGuid}`, {
      returnTo: '/checklist-reports',
      initialTab: t('CheckList')
    });

  const { items, isBusy, count, result } = checklistReportStore.state;

  const getCompletionColor = (completed: number, total: number) => {
    if (total === 0) return 'text-gray-400';
    const pct = (completed / total) * 100;
    if (pct >= 100) return 'text-green-600';
    if (pct >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-3 px-2">
      <Dimmer active={isBusy} style={{ zIndex: 999 }}>
        <Loader>{t('Loading')}</Loader>
      </Dimmer>

      {result && !result.isSuccess && (result.messages || []).length > 0 && (
        <Message
          className="error-message__style"
          icon="exclamation circle"
          error
          header={t('An error ocurred')}
          list={result.messages.map(o => o.body)}
          onDismiss={checklistReportStore.clearMessages}
        />
      )}

      <div className="flex justify-end gap-2">
        <button
          className="checklist-reports-mobile-view-mode flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded disabled:opacity-40"
          disabled={isBusy}
          onClick={toggleViewMode}
        >
          <Icon name={grouped ? 'list' : 'sitemap'} />
          {grouped ? t('Show flat rows') : t('Show grouped events')}
        </button>
        {onReset && (
          <button
            className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded disabled:opacity-40"
            disabled={isBusy}
            onClick={onReset}
          >
            <Icon name="sort" />
            {t('Reset')}
          </button>
        )}
        <button
          className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded disabled:opacity-40"
          disabled={isBusy || items.length === 0}
          onClick={handleExport}
        >
          <Icon name="download" />
          {t('Export')}
        </button>
      </div>

      {items.length === 0 && !isBusy && <p className="text-center text-gray-500 py-8">{t('No checklist records found')}</p>}

      {items.map((itemModel, i) => {
        const item = itemModel.item;
        const isGroupedEvent = 'checklistItems' in item;
        return (
          <div key={item.eventGuid || i} className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <span className="font-semibold text-gray-800 text-sm">{item.eventType}</span>
              <span className={`text-sm font-bold ${getCompletionColor(item.completedChecklistItems, item.totalChecklistItems)}`}>
                {item.completedChecklistItems}/{item.totalChecklistItems}
              </span>
            </div>
            <button type="button" className="text-xs text-blue-600 font-medium mb-1" onClick={() => openEvent(item.eventGuid)}>
              {item.eventId}
            </button>
            <div className="text-xs text-gray-600 mb-1">
              {item.machineModel} — {item.location}
            </div>
            {isGroupedEvent ? (
              <details className="mt-2 border-t border-gray-200 pt-2">
                <summary className="cursor-pointer text-xs text-gray-700">
                  {t('CheckList Item')} ({item.checklistItems.length})
                </summary>
                <div className="space-y-2 pt-2">
                  {item.checklistItems.map(checklistItem => (
                    <div key={checklistItem.id} className="border-l-2 border-gray-300 pl-2 text-xs">
                      <div className="flex items-center gap-1 text-blue-500">
                        <Icon
                          name={checklistItem.checked ? 'check square outline' : 'square outline'}
                          color={checklistItem.checked ? 'green' : 'grey'}
                        />
                        {checklistItem.checkListItem}
                      </div>
                      <div className="text-gray-600">
                        {checklistItem.instructor} - {checklistItem.status}
                      </div>
                      {checklistItem.comment && <div className="text-gray-500 italic">{checklistItem.comment}</div>}
                    </div>
                  ))}
                </div>
              </details>
            ) : (
              <div className="mt-2 border-t border-gray-200 pt-2 text-xs">
                <div className="text-gray-600">
                  {item.instructor} - {item.status}
                </div>
                <div className="flex items-center gap-1 text-blue-500">
                  <Icon name={item.checked ? 'check square outline' : 'square outline'} color={item.checked ? 'green' : 'grey'} />
                  {item.checkListItem}
                </div>
                {item.comment && <div className="text-gray-500 italic">{item.comment}</div>}
              </div>
            )}
          </div>
        );
      })}

      {count > take && (
        <div className="flex justify-between items-center pt-2">
          <button
            className="px-4 py-2 text-sm bg-gray-100 rounded disabled:opacity-40"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - take))}
          >
            {t('Previous')}
          </button>
          <span className="text-xs text-gray-500">
            {skip + 1}–{Math.min(skip + take, count)} / {count}
          </span>
          <button
            className="px-4 py-2 text-sm bg-gray-100 rounded disabled:opacity-40"
            disabled={skip + take >= count}
            onClick={() => setSkip(skip + take)}
          >
            {t('Next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default connect(['checklistReportStore', ChecklistReportStore])(ChecklistReportListMobile);
